import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import DocumentHeaderButton from 'Components/DocumentHeaderButton';
import { createPitchToZoom } from 'Components/Interact/common/pitchToZoom';
import {
  calculatePreviewScale,
  updateScrollOnScale,
} from 'Components/Interact/common/utils';
import { FieldItem } from 'Components/Interact/components/FieldItem';
import ScaleDropDown from 'Components/Interact/components/ScaleDropDown';
import { PDFDocument } from 'Components/PDFDocument';
import UIButton from 'Components/UIComponents/UIButton';
import UISpinner from 'Components/UIComponents/UISpinner';
import { useDownloadFiles, useIsMobile, useIsTablet } from 'Hooks/Common';
import {
  useDocumentActivitiesDownload,
  useDocumentActivitiesSeparateSign,
  useDocumentDownload,
  useDocumentGuard,
  useDocumentInteractInit,
  useDocumentPrint,
  useDocumentSeparateDownload,
} from 'Hooks/Document';
import { useDocumentFieldUpdateLocally } from 'Hooks/DocumentFields';
import { Document, DocumentDownloadTypes, DocumentStatuses } from 'Interfaces/Document';
import { DocumentField } from 'Interfaces/DocumentFields';
import { AuthorizedRoutePaths } from 'Interfaces/RoutePaths';
import { throttle } from 'lodash-es';
import { documentFileKeyExtractorForDocument } from 'Pages/Documents/extractors/DocumentFileKeyExtractor';
import { PDFDocumentProxy } from 'pdfjs-dist';
import Toast from 'Services/Toast';
import {
  selectDocument,
  selectDocumentFields,
  selectOwnDocumentPdfMetadataFromDocumentPart,
} from 'Utils/selectors';

import { DocumentPreviewPage } from '../../Components/DocumentPage';

import DocumentActivityList from './components/DocumentActivityList';

import DownloadIcon from 'Assets/images/icons/doc-download-icon.svg';
import PrintIcon from 'Assets/images/icons/print-icon.svg';

type DocumentParams = {
  documentId: Document['id'];
};

const sizes = {
  pageContainerWidth: window.innerWidth < 768 ? window.innerWidth : 955,
  pageMarginBottom: 20,
};

const DocumentPreview = () => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const navigate = useNavigate();
  const params = useParams<DocumentParams>();

  const documentId = params.documentId;
  const currentDocument = useSelector(state => selectDocument(state, { documentId }));
  const [downloadDocuments] = useDownloadFiles<Document>({
    fileExtractors: [documentFileKeyExtractorForDocument],
  });
  const isDownloadEnable = useMemo(
    () =>
      !!currentDocument &&
      (!!currentDocument?.resultPdfFileKey ||
        currentDocument.parts.filter(x => x.filesUploaded).length > 0),
    [currentDocument],
  );
  const isPrintEnable = useMemo(
    () =>
      currentDocument?.status !== DocumentStatuses.DRAFT &&
      (!!currentDocument?.resultPdfFileKey ||
        !!currentDocument?.resultDocumentPdfFileKey),
    [currentDocument],
  );

  const [downloadSeparateDocument] = useDocumentSeparateDownload();
  const [, isSigningSeparateDocumentActivities] = useDocumentActivitiesSeparateSign();
  const [downloadDocumentActivities] = useDocumentActivitiesDownload();
  const [downloadDocument, isDownloadingDocument] = useDocumentDownload();
  const [printPdf, isPdfLoading] = useDocumentPrint(currentDocument as Document);
  const [
    initDocumentInteract,
    isDocumentInteractInitializing,
  ] = useDocumentInteractInit();
  const documentFields: DocumentField[] = useSelector(selectDocumentFields);
  const pdfPageMeta = useSelector(state =>
    selectOwnDocumentPdfMetadataFromDocumentPart(state, { documentId }),
  );

  const [pagesContainer, setPagesContainer] = useState<HTMLDivElement | null>(null);
  const virtuosoRef = useRef(null);

  const greaterPageWidth = useMemo<number>(() => {
    return Object.values(pdfPageMeta).reduce((greaterWidth, pageMetadata) => {
      return greaterWidth < pageMetadata.width ? pageMetadata.width : greaterWidth;
    }, 0);
  }, [pdfPageMeta]);

  const [pdfUrls, setPdfUrls] = useState<string[] | undefined>();
  const [pageCount, setPageCount] = useState<number>(0);
  const [scaleCoefficient, setScaleCoefficient] = useState(1);
  const [baseScale, setBaseScale] = useState(() => {
    return calculatePreviewScale(greaterPageWidth, pagesContainer) || scaleCoefficient;
  });

  const isDownloadActivitiesEnable = useMemo(() => {
    return (
      !!documentId &&
      currentDocument?.status === DocumentStatuses.COMPLETED &&
      !!currentDocument?.resultActivitiesPdfFileKey &&
      currentDocument?.downloadType === DocumentDownloadTypes.SEPARATED
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, currentDocument, !!currentDocument?.resultActivitiesPdfFileKey]);

  const documentScale = baseScale * scaleCoefficient;

  const handleScaleCoefficientChange = useCallback(
    (nextDocumentScale: number, anchor?: { clientX: number; clientY: number }) => {
      updateScrollOnScale({
        element: pagesContainer,
        anchor,
        documentScale,
        nextDocumentScale,
      });

      setScaleCoefficient(nextDocumentScale);
    },
    [pagesContainer, documentScale],
  );

  useEffect(() => {
    if (!pagesContainer) return;

    const pitchToZoom = createPitchToZoom(pagesContainer, {
      scale: scaleCoefficient,
      onTouchMoveComplete: handleScaleCoefficientChange,
    });
    pitchToZoom.attachListeners();
    return () => void pitchToZoom.detachListeners();
  }, [pagesContainer, scaleCoefficient, handleScaleCoefficientChange]);

  const updateContainerBaseRatio = useCallback(
    (container: HTMLElement | null) => {
      const baseScale =
        (container?.clientWidth || sizes.pageContainerWidth) / greaterPageWidth;

      if (typeof baseScale !== 'undefined' && baseScale > 0) {
        setBaseScale(baseScale);
      }
    },
    [greaterPageWidth],
  );

  useEffect(() => {
    if (!pagesContainer) return;

    function resize() {
      updateContainerBaseRatio(pagesContainer);
    }

    resize();
    const throttledResize = throttle(resize, 750);

    window.addEventListener('resize', throttledResize);
    return () => window.removeEventListener('resize', throttledResize);
  }, [pagesContainer, updateContainerBaseRatio]);

  const updateDocumentFieldLocally = useDocumentFieldUpdateLocally();

  const handleDocumentNotFound = useCallback(() => {
    Toast.error('Document does not exist', { toastId: 'document_not_found_error' });
    navigate(AuthorizedRoutePaths.DOCUMENTS);
  }, [navigate]);

  const handleSeparateDocumentDownload = useCallback(async () => {
    if (!currentDocument || !currentDocument.id) return;

    downloadSeparateDocument({ documentId: currentDocument.id }).catch(err => {
      Toast.handleErrors(err, { toastId: 'separated_download_error' });
    });
  }, [currentDocument, downloadSeparateDocument]);

  const handleDocumentDownload = useCallback(() => {
    const promise =
      currentDocument && currentDocument.id && currentDocument.resultPdfFileKey
        ? downloadDocument({ documentId: currentDocument.id })
        : currentDocument && isDownloadEnable
        ? ((downloadDocuments([currentDocument]) as unknown) as Promise<void>)
        : Promise.resolve();

    promise.catch(err => {
      Toast.handleErrors(err, { toastId: 'download_error' });
    });
  }, [currentDocument, isDownloadEnable, downloadDocument, downloadDocuments]);

  const isCheckingDocument = useDocumentGuard({
    documentId,
    onFailure: handleDocumentNotFound,
  });

  const handleGetConvertedDocument = useCallback(async (document: Document) => {
    initDocumentInteract({ document })
      .then(({ 0: pdfUrls }) => {
        setPdfUrls(pdfUrls);
      })
      .catch(err => {
        Toast.handleErrors(err, { toastId: 'document_interact_error' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDocumentActivitiesDownload = useCallback(async () => {
    if (!documentId) return;
    downloadDocumentActivities({ documentId }).catch(err => {
      Toast.handleErrors(err, { toastId: 'activities_download_error' });
    });
  }, [documentId, downloadDocumentActivities]);

  useEffect(() => {
    if (currentDocument) {
      handleGetConvertedDocument(currentDocument);
    }
  }, [currentDocument, handleGetConvertedDocument]);

  const calculateDocumentFieldStyles = useCallback(
    (documentField: DocumentField) => {
      const updatedField = {
        ...documentField,
        style: {
          ...documentField.style,
          width: documentField.width as number,
          height: documentField.height as number,
          maxWidth: pdfPageMeta[documentField.pageNumber].width,
          maxHeight: pdfPageMeta[documentField.pageNumber].height,
          left: documentField.coordinateX,
          top: documentField.coordinateY,
          fontFamily: documentField.fontFamily || undefined,
          fontSize: documentField.fontSize ? documentField.fontSize : undefined,
        },
      };

      updateDocumentFieldLocally(updatedField);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateDocumentFieldLocally, pagesContainer],
  );

  const handlePdfLoadSuccess = useCallback((pdfDocumentProxy: PDFDocumentProxy) => {
    setPageCount(pdfDocumentProxy.numPages);
  }, []);

  useEffect(() => {
    if (pagesContainer) {
      documentFields.forEach(calculateDocumentFieldStyles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesContainer, calculateDocumentFieldStyles]);

  if (isCheckingDocument) {
    return <UISpinner wrapperClassName="spinner--main__wrapper" width={50} height={50} />;
  }

  return (
    <div className="documentPreview__wrapper">
      <div className="documentPreview__document-wrapper">
        <div className="documentPreview__document-inner">
          <header className="documentPreview__document-header">
            <p className="documentPreview__document-header-title">Document Preview</p>
            <div className="documentPreview__document-header-scale">
              <ScaleDropDown
                changeScale={setScaleCoefficient}
                documentScale={scaleCoefficient}
              />
            </div>
            <div className="documentPreview__document-header-right">
              <div className="documentPreview__document-header-button-wrapper">
                {isDownloadEnable &&
                  currentDocument?.status === DocumentStatuses.COMPLETED && (
                    <DocumentHeaderButton
                      title="Download"
                      icon={DownloadIcon}
                      collapsed={isMobile}
                      onClick={
                        currentDocument.downloadType === DocumentDownloadTypes.SEPARATED
                          ? handleSeparateDocumentDownload
                          : handleDocumentDownload
                      }
                      disabled={isDownloadingDocument}
                      isLoading={isDownloadingDocument}
                      iconType="stroke"
                    />
                  )}
                {isPrintEnable && (
                  <DocumentHeaderButton
                    title="Print"
                    icon={PrintIcon}
                    collapsed={isMobile}
                    onClick={printPdf}
                    disabled={isPdfLoading}
                    isLoading={isPdfLoading}
                  />
                )}
              </div>
              <UIButton
                priority="primary"
                title={isMobile ? '↩' : 'Back to Documents'}
                handleClick={() => navigate(AuthorizedRoutePaths.DOCUMENTS)}
              />
            </div>
          </header>
          {isDocumentInteractInitializing ? (
            <div className="documentPreview__spinner">
              <UISpinner
                width={50}
                height={50}
                wrapperClassName="spinner--main__wrapper"
              />
            </div>
          ) : (
            <div ref={setPagesContainer} className="documentPreview__document-container">
              <div
                className="documentPreview__pages-container"
                style={{ width: greaterPageWidth * documentScale }}
              >
                {pdfUrls &&
                  pdfUrls.map((file, index) => (
                    <PDFDocument
                      key={index}
                      file={file}
                      onLoadSuccess={handlePdfLoadSuccess}
                      externalLinkTarget="_blank"
                    >
                      <Virtuoso
                        key={scaleCoefficient}
                        ref={virtuosoRef}
                        style={{
                          height: window.innerHeight - (isTablet ? 175 : 245),
                          width: greaterPageWidth * (baseScale || 1),
                        }}
                        totalCount={pageCount}
                        increaseViewportBy={500}
                        itemContent={index => {
                          const pageNumber = index + 1;
                          const pageMeta = pdfPageMeta && pdfPageMeta[pageNumber];
                          const pageFields = documentFields.filter(
                            field => field.pageNumber === pageNumber,
                          );

                          return (
                            <DocumentPreviewPage
                              scale={documentScale}
                              key={pageNumber}
                              pageNumber={pageNumber}
                              style={{
                                ...pdfPageMeta[pageNumber],
                                // TODO: height и width это заглушка пока
                                // не пофиксится баг с вклеинными страницами документ активностей,
                                // которые не пересчитывают метадату документа
                                height:
                                  pageMeta && pageMeta.height ? pageMeta.height : 842,
                                width: pageMeta && pageMeta.width ? pageMeta.width : 595,
                                marginBottom: sizes.pageMarginBottom,
                              }}
                            >
                              {currentDocument?.status !== DocumentStatuses.COMPLETED &&
                                pageFields.map(field => (
                                  <FieldItem
                                    documentScale={documentScale}
                                    selectedScale={scaleCoefficient}
                                    key={field.id}
                                    field={field}
                                    disabled
                                    onChangeSigner={() => {}}
                                    isResizable={false}
                                  />
                                ))}
                            </DocumentPreviewPage>
                          );
                        }}
                      />
                    </PDFDocument>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {documentId && (
        <DocumentActivityList
          documentId={documentId}
          canDownloadActivities={isDownloadActivitiesEnable}
          handleDocumentActivitiesDownload={handleDocumentActivitiesDownload}
          isDownloading={isSigningSeparateDocumentActivities}
          isDocumentCompleted={currentDocument?.status === DocumentStatuses.COMPLETED}
        />
      )}
    </div>
  );
};

export default DocumentPreview;
